/**
 * businessInsightsSectionsData.ts — Tarjetas demo nuevas de las secciones
 * Rentabilidad, Inventario, Clientes, Equipo y Operación.
 *
 * QUÉ HACE: declara los datasets del explorador que completan las gráficas de
 *   cada sección del hub: evolución y riesgo de margen, ABC y cobertura de
 *   inventario, clientes inactivos y ventas por segmento, productividad,
 *   asistencia y tareas del equipo, y eficiencia de la operación. Son datos DEMO
 *   deterministas; cuando el backend exponga los agregados, este archivo es el
 *   único que cambia.
 *
 * QUÉ INVOCA: helpers de `businessInsights.shared.ts`.
 * QUÉ EXPORTA: BUSINESS_INSIGHTS_SECTIONS (readonly BusinessInsightDataset[]).
 * QUÉ NO DEBE HACER: no lee storage, no hace fetch, no conoce React.
 */
import {
  Activity,
  AlertTriangle,
  BadgePercent,
  Boxes,
  CalendarClock,
  ClipboardCheck,
  Clock3,
  Coins,
  Gift,
  Landmark,
  PackageCheck,
  Percent,
  Receipt,
  Scale,
  ShoppingBag,
  Timer,
  TrendingUp,
  UserCheck,
  UserMinus,
  Users,
} from "lucide-react";
import type { BusinessInsightDataset } from "../types/businessInsights.types";
import {
  INSIGHT_COLORS,
  INSIGHT_VIEW_PRESETS,
  insightRow,
} from "./businessInsights.shared";

export const BUSINESS_INSIGHTS_SECTIONS: readonly BusinessInsightDataset[] = [
  /* ── Análisis del negocio ───────────────────────────────────────────────── */
  {
    id: "cambios-negocio",
    title: "Cambios importantes",
    subtitle: "Variación vs período anterior — todas las áreas",
    icon: TrendingUp,
    group: "ventas",
    valueKind: "decimal",
    valueSuffix: "%",
    nameLabel: "Área",
    series: [
      { key: "variacion", label: "Variación", color: INSIGHT_COLORS.caja },
    ],
    rows: [
      insightRow("Ventas", { variacion: 12 }),
      insightRow("Web", { variacion: 31 }),
      insightRow("Utilidad", { variacion: 13 }),
      insightRow("Margen", { variacion: -4.2 }),
      insightRow("Clientes nuevos", { variacion: -18 }),
      insightRow("Cancelaciones", { variacion: 2.1 }),
      insightRow("Gastos", { variacion: 0.3 }),
      insightRow("Ticket promedio", { variacion: -3.1 }),
    ],
    views: ["barHorizontal", "bar", "tabla", "line", "matriz"],
    colorBySign: true,
    badge: { mode: "min", label: "Mayor caída", tone: "red" },
  },

  /* ── Rentabilidad ───────────────────────────────────────────────────────── */
  {
    id: "margen-evolucion",
    title: "Evolución del margen",
    subtitle: "Margen bruto % — últimos 12 meses",
    icon: Percent,
    group: "rentabilidad",
    valueKind: "decimal",
    valueSuffix: "%",
    nameLabel: "Mes",
    series: [{ key: "margen", label: "Margen %", color: INSIGHT_COLORS.exito }],
    rows: [
      insightRow("Ene", { margen: 31.2 }),
      insightRow("Feb", { margen: 32.8 }),
      insightRow("Mar", { margen: 33.6 }),
      insightRow("Abr", { margen: 31.9 }),
      insightRow("May", { margen: 34.2 }),
      insightRow("Jun", { margen: 33.1 }),
      insightRow("Jul", { margen: 34.8 }),
      insightRow("Ago", { margen: 33.2 }),
      insightRow("Sep", { margen: 34.4 }),
    ],
    views: INSIGHT_VIEW_PRESETS.temporalLineFirst,
    badge: { mode: "max", label: "Mejor mes", tone: "green" },
  },
  {
    id: "margen-peligroso",
    title: "Productos con margen peligroso",
    subtitle: "Margen % por producto — revisar precio o costo",
    icon: AlertTriangle,
    group: "rentabilidad",
    valueKind: "decimal",
    valueSuffix: "%",
    nameLabel: "Producto",
    series: [
      { key: "margen", label: "Margen %", color: INSIGHT_COLORS.gastos },
    ],
    rows: [
      insightRow("Combo Liquidación A", { margen: -10 }),
      insightRow("Adiciones al Pedido", { margen: 0 }),
      insightRow("Cafetera 12 tazas", { margen: 8.4 }),
      insightRow("Freidora de aire", { margen: 11.2 }),
      insightRow("Plancha cerámica", { margen: 12.6 }),
      insightRow("Ventilador torre", { margen: 14.1 }),
    ],
    views: INSIGHT_VIEW_PRESETS.rankingHorizontal,
    colorBySign: true,
    badge: { mode: "min", label: "Más crítico", tone: "red" },
  },
  {
    id: "categoria-ingreso-costo",
    title: "Ingresos vs costo por categoría",
    subtitle: "Ingreso, costo y margen — último mes",
    icon: Coins,
    group: "rentabilidad",
    valueKind: "currency",
    nameLabel: "Categoría",
    series: [
      { key: "ingresos", label: "Ingresos", color: INSIGHT_COLORS.caja },
      { key: "costo", label: "Costo", color: INSIGHT_COLORS.gastos },
      { key: "margen", label: "Margen", color: INSIGHT_COLORS.exito },
    ],
    rows: [
      insightRow("Tecnología", {
        ingresos: 256600000,
        costo: 171000000,
        margen: 85600000,
      }),
      insightRow("Joyería", {
        ingresos: 164200000,
        costo: 108000000,
        margen: 56200000,
      }),
      insightRow("Electrodomésticos", {
        ingresos: 78200000,
        costo: 52000000,
        margen: 26200000,
      }),
      insightRow("Muebles", {
        ingresos: 66000000,
        costo: 44000000,
        margen: 22000000,
      }),
      insightRow("Belleza", {
        ingresos: 32000000,
        costo: 21000000,
        margen: 11000000,
      }),
      insightRow("Otros", {
        ingresos: 154800000,
        costo: 103000000,
        margen: 51800000,
      }),
    ],
    views: INSIGHT_VIEW_PRESETS.ranking,
    badge: {
      mode: "max",
      label: "Mayor margen",
      tone: "green",
      seriesKey: "margen",
    },
    sliceKey: "margen",
  },
  {
    id: "impacto-descuentos",
    title: "Impacto de descuentos",
    subtitle: "Ventas y descuento por categoría — último mes",
    icon: BadgePercent,
    group: "rentabilidad",
    valueKind: "currency",
    nameLabel: "Categoría",
    series: [
      { key: "ventas", label: "Ventas", color: INSIGHT_COLORS.caja },
      { key: "descuento", label: "Descuento", color: INSIGHT_COLORS.alerta },
    ],
    rows: [
      insightRow("Tecnología", { ventas: 256600000, descuento: 6000000 }),
      insightRow("Electrodomésticos", { ventas: 78200000, descuento: 3000000 }),
      insightRow("Joyería", { ventas: 164200000, descuento: 2100000 }),
      insightRow("Belleza", { ventas: 32000000, descuento: 500000 }),
      insightRow("Muebles", { ventas: 66000000, descuento: 400000 }),
    ],
    views: INSIGHT_VIEW_PRESETS.ranking,
    badge: {
      mode: "max",
      label: "Más descuento",
      tone: "amber",
      seriesKey: "descuento",
    },
    sliceKey: "descuento",
  },
  {
    id: "margen-canal",
    title: "Margen por canal",
    subtitle: "Ingresos, costo y margen — Caja vs Web",
    icon: Percent,
    group: "rentabilidad",
    valueKind: "currency",
    nameLabel: "Canal",
    series: [
      { key: "ingresos", label: "Ingresos", color: INSIGHT_COLORS.caja },
      { key: "costo", label: "Costo", color: INSIGHT_COLORS.gastos },
      { key: "margen", label: "Margen", color: INSIGHT_COLORS.exito },
    ],
    rows: [
      insightRow("Caja", {
        ingresos: 1704643044,
        costo: 1118290000,
        margen: 586353044,
      }),
      insightRow("Web", {
        ingresos: 480796756,
        costo: 315415000,
        margen: 165381756,
      }),
    ],
    views: INSIGHT_VIEW_PRESETS.ranking,
    badge: {
      mode: "max",
      label: "Mayor margen",
      tone: "green",
      seriesKey: "margen",
    },
    sliceKey: "margen",
  },

  /* ── Inventario ─────────────────────────────────────────────────────────── */
  {
    id: "abc-inventario",
    title: "Clasificación ABC",
    subtitle: "Productos vs participación en ventas",
    icon: Boxes,
    group: "productos",
    valueKind: "decimal",
    valueSuffix: "%",
    nameLabel: "Clase",
    series: [
      {
        key: "productos",
        label: "% de productos",
        color: INSIGHT_COLORS.neutro,
      },
      { key: "ventas", label: "% de ventas", color: INSIGHT_COLORS.exito },
    ],
    rows: [
      insightRow("A", { productos: 20, ventas: 72 }),
      insightRow("B", { productos: 30, ventas: 20 }),
      insightRow("C", { productos: 50, ventas: 8 }),
    ],
    views: INSIGHT_VIEW_PRESETS.distribucion,
    badge: {
      mode: "max",
      label: "Clase clave",
      tone: "green",
      seriesKey: "ventas",
    },
  },
  {
    id: "cobertura-stock",
    title: "Cobertura de stock",
    subtitle: "Días de inventario restantes por producto",
    icon: CalendarClock,
    group: "productos",
    valueKind: "decimal",
    valueSuffix: " días",
    nameLabel: "Producto",
    series: [
      { key: "cobertura", label: "Cobertura", color: INSIGHT_COLORS.info },
    ],
    rows: [
      insightRow("Nevera No Frost 400L", { cobertura: 3 }),
      insightRow("Cafetera 12 tazas", { cobertura: 5 }),
      insightRow("Freidora de aire", { cobertura: 6 }),
      insightRow("Parlante Bluetooth", { cobertura: 7 }),
      insightRow('TV 55" Smart', { cobertura: 10 }),
      insightRow("Licuadora 2L", { cobertura: 15 }),
      insightRow("Lavadora 18kg", { cobertura: 30 }),
    ],
    views: ["barHorizontal", "semaforo", ...INSIGHT_VIEW_PRESETS.ranking],
    status: {
      valueKey: "cobertura",
      okAt: 10,
      warnAt: 5,
      labels: { ok: "Cubierto", warn: "Por agotarse", bad: "Crítico" },
    },
    badge: { mode: "min", label: "Más crítico", tone: "red" },
  },
  /* ── Clientes ───────────────────────────────────────────────────────────── */
  {
    id: "clientes-inactivos",
    title: "Clientes inactivos",
    subtitle: "Clientes sin compra por rango de días",
    icon: UserMinus,
    group: "clientes",
    valueKind: "integer",
    nameLabel: "Última compra",
    series: [
      { key: "clientes", label: "Clientes", color: INSIGHT_COLORS.alerta },
    ],
    rows: [
      insightRow("30 días", { clientes: 124 }),
      insightRow("60 días", { clientes: 76 }),
      insightRow("90 días", { clientes: 42 }),
      insightRow("180 días", { clientes: 18 }),
    ],
    views: INSIGHT_VIEW_PRESETS.distribucion,
    badge: { mode: "max", label: "Por recuperar", tone: "amber" },
  },
  {
    id: "ventas-segmento",
    title: "Ventas por segmento",
    subtitle: "Ingresos según tipo de cliente — último mes",
    icon: Users,
    group: "clientes",
    valueKind: "currency",
    nameLabel: "Segmento",
    series: [{ key: "ventas", label: "Ventas", color: INSIGHT_COLORS.violeta }],
    rows: [
      insightRow("VIP", { ventas: 82000000 }),
      insightRow("Recurrentes", { ventas: 146000000 }),
      insightRow("Nuevos", { ventas: 61000000 }),
      insightRow("Ocasionales", { ventas: 32000000 }),
    ],
    views: INSIGHT_VIEW_PRESETS.distribucion,
    badge: { mode: "max", label: "Mayor segmento", tone: "green" },
  },
  {
    id: "fidelizacion-clientes",
    title: "Programa de fidelización",
    subtitle: "Puntos emitidos y canjeados — últimos 6 meses",
    icon: Gift,
    group: "clientes",
    valueKind: "integer",
    valueSuffix: " pts",
    nameLabel: "Mes",
    series: [
      { key: "emitidos", label: "Emitidos", color: INSIGHT_COLORS.violeta },
      { key: "canjeados", label: "Canjeados", color: INSIGHT_COLORS.exito },
    ],
    rows: [
      insightRow("Ene", { emitidos: 980, canjeados: 360 }),
      insightRow("Feb", { emitidos: 1120, canjeados: 410 }),
      insightRow("Mar", { emitidos: 1240, canjeados: 520 }),
      insightRow("Abr", { emitidos: 1180, canjeados: 470 }),
      insightRow("May", { emitidos: 1420, canjeados: 610 }),
      insightRow("Jun", { emitidos: 1560, canjeados: 690 }),
    ],
    views: INSIGHT_VIEW_PRESETS.temporalSinVelas,
    badge: {
      mode: "total",
      label: "Puntos emitidos",
      tone: "blue",
      seriesKey: "emitidos",
    },
  },

  /* ── Equipo ─────────────────────────────────────────────────────────────── */
  {
    id: "productividad-hora",
    title: "Ventas por hora trabajada",
    subtitle: "Productividad por empleado — último mes",
    icon: Timer,
    group: "equipo",
    valueKind: "currency",
    nameLabel: "Empleado",
    series: [
      { key: "ventasHora", label: "Ventas/hora", color: INSIGHT_COLORS.exito },
    ],
    rows: [
      insightRow("Yinet Alarcón", { ventasHora: 185625 }),
      insightRow("Carlos Ruiz", { ventasHora: 163100 }),
      insightRow("Sofía Castro", { ventasHora: 150633 }),
      insightRow("Luz Marina", { ventasHora: 140624 }),
      insightRow("Juan Pérez", { ventasHora: 54864 }),
    ],
    views: INSIGHT_VIEW_PRESETS.rankingHorizontal,
    badge: { mode: "max", label: "Más productivo", tone: "green" },
  },
  {
    id: "asistencia-equipo",
    title: "Asistencia y puntualidad",
    subtitle: "% de asistencia por empleado — último mes",
    icon: UserCheck,
    group: "equipo",
    valueKind: "decimal",
    valueSuffix: "%",
    nameLabel: "Empleado",
    series: [
      { key: "asistencia", label: "Asistencia", color: INSIGHT_COLORS.info },
    ],
    rows: [
      insightRow("Sofía Castro", { asistencia: 100 }),
      insightRow("Yinet Alarcón", { asistencia: 98 }),
      insightRow("Luz Marina", { asistencia: 96 }),
      insightRow("Carlos Ruiz", { asistencia: 94 }),
      insightRow("Juan Pérez", { asistencia: 88 }),
    ],
    views: ["barHorizontal", "semaforo", ...INSIGHT_VIEW_PRESETS.ranking],
    status: {
      valueKey: "asistencia",
      okAt: 95,
      warnAt: 90,
      labels: { ok: "Al día", warn: "Vigilar", bad: "Revisar" },
    },
    badge: { mode: "max", label: "Mejor asistencia", tone: "green" },
  },
  {
    id: "tareas-equipo",
    title: "Cumplimiento de tareas",
    subtitle: "Tareas del equipo por estado — mes en curso",
    icon: ClipboardCheck,
    group: "equipo",
    valueKind: "integer",
    nameLabel: "Estado",
    series: [{ key: "tareas", label: "Tareas", color: INSIGHT_COLORS.exito }],
    rows: [
      insightRow("Completadas", { tareas: 46 }),
      insightRow("Atrasadas", { tareas: 3 }),
      insightRow("Pendientes", { tareas: 1 }),
    ],
    views: INSIGHT_VIEW_PRESETS.distribucion,
    badge: { mode: "total", label: "Total tareas", tone: "green" },
  },
  {
    id: "carga-trabajo",
    title: "Carga de trabajo",
    subtitle: "Demanda por turno vs personal disponible",
    icon: Scale,
    group: "equipo",
    valueKind: "integer",
    nameLabel: "Turno",
    series: [
      { key: "demanda", label: "Demanda", color: INSIGHT_COLORS.caja },
      { key: "personal", label: "Personal", color: INSIGHT_COLORS.neutro },
    ],
    rows: [
      insightRow("Mañana", { demanda: 32, personal: 4 }),
      insightRow("Tarde", { demanda: 54, personal: 4 }),
      insightRow("Noche", { demanda: 21, personal: 3 }),
    ],
    views: INSIGHT_VIEW_PRESETS.ranking,
    badge: {
      mode: "max",
      label: "Turno fuerte",
      tone: "amber",
      seriesKey: "demanda",
    },
  },
  {
    id: "ticket-empleado",
    title: "Ticket promedio por empleado",
    subtitle: "Valor promedio por venta — último mes",
    icon: Receipt,
    group: "equipo",
    valueKind: "currency",
    nameLabel: "Empleado",
    series: [{ key: "ticket", label: "Ticket", color: INSIGHT_COLORS.violeta }],
    rows: [
      insightRow("Yinet Alarcón", { ticket: 988660 }),
      insightRow("Luz Marina", { ticket: 937496 }),
      insightRow("Carlos Ruiz", { ticket: 826445 }),
      insightRow("Sofía Castro", { ticket: 803378 }),
      insightRow("Juan Pérez", { ticket: 548639 }),
    ],
    views: INSIGHT_VIEW_PRESETS.rankingHorizontal,
    badge: { mode: "max", label: "Mejor ticket", tone: "green" },
  },
  /* ── Operación ──────────────────────────────────────────────────────────── */
  {
    id: "pedidos-estado",
    title: "Pedidos por estado",
    subtitle: "Pedidos de la tienda por etapa — hoy",
    icon: ShoppingBag,
    group: "operacion",
    valueKind: "integer",
    nameLabel: "Estado",
    series: [{ key: "pedidos", label: "Pedidos", color: INSIGHT_COLORS.info }],
    rows: [
      insightRow("Recibidos", { pedidos: 32 }),
      insightRow("En preparación", { pedidos: 18 }),
      insightRow("Listos", { pedidos: 7 }),
      insightRow("Despachados", { pedidos: 41 }),
      insightRow("Cancelados", { pedidos: 2 }),
    ],
    views: INSIGHT_VIEW_PRESETS.ranking,
    badge: { mode: "max", label: "Mayor volumen", tone: "green" },
  },
  {
    id: "tiempo-preparacion",
    title: "Tiempo de preparación",
    subtitle: "Pedidos por rango de minutos — último mes",
    icon: Timer,
    group: "operacion",
    valueKind: "decimal",
    valueSuffix: "%",
    nameLabel: "Rango",
    series: [
      { key: "pedidos", label: "Pedidos", color: INSIGHT_COLORS.alerta },
    ],
    rows: [
      insightRow("12–15 min", { pedidos: 62 }),
      insightRow("16–20 min", { pedidos: 24 }),
      insightRow("21+ min", { pedidos: 14 }),
    ],
    views: INSIGHT_VIEW_PRESETS.distribucion,
    badge: { mode: "max", label: "Mayoría", tone: "green" },
  },
  {
    id: "tiempo-atencion",
    title: "Tiempo de atención",
    subtitle: "Duración promedio por canal",
    icon: Clock3,
    group: "operacion",
    valueKind: "decimal",
    valueSuffix: " min",
    nameLabel: "Canal",
    series: [{ key: "minutos", label: "Minutos", color: INSIGHT_COLORS.info }],
    rows: [
      insightRow("Caja", { minutos: 3.3 }),
      insightRow("Mesa", { minutos: 8.7 }),
      insightRow("Web", { minutos: 12.2 }),
      insightRow("Domicilio", { minutos: 28 }),
    ],
    views: INSIGHT_VIEW_PRESETS.rankingHorizontal,
    badge: { mode: "max", label: "Más lento", tone: "amber" },
  },
  {
    id: "eficiencia-pedidos",
    title: "Eficiencia de pedidos",
    subtitle: "Recibidos, completados, cancelados y pendientes",
    icon: PackageCheck,
    group: "operacion",
    valueKind: "integer",
    nameLabel: "Estado",
    series: [{ key: "pedidos", label: "Pedidos", color: INSIGHT_COLORS.exito }],
    rows: [
      insightRow("Recibidos", { pedidos: 1240 }),
      insightRow("Completados", { pedidos: 1198 }),
      insightRow("Cancelados", { pedidos: 22 }),
      insightRow("Pendientes", { pedidos: 20 }),
    ],
    views: INSIGHT_VIEW_PRESETS.distribucion,
    badge: { mode: "max", label: "Completados", tone: "green" },
  },
  {
    id: "demanda-capacidad",
    title: "Demanda vs capacidad",
    subtitle: "Pedidos por hora vs capacidad del equipo",
    icon: Activity,
    group: "operacion",
    valueKind: "integer",
    nameLabel: "Hora",
    series: [
      { key: "demanda", label: "Demanda", color: INSIGHT_COLORS.caja },
      { key: "capacidad", label: "Capacidad", color: INSIGHT_COLORS.neutro },
    ],
    rows: [
      insightRow("10 AM", { demanda: 5, capacidad: 7 }),
      insightRow("12 PM", { demanda: 11, capacidad: 7 }),
      insightRow("2 PM", { demanda: 12, capacidad: 7 }),
      insightRow("4 PM", { demanda: 9, capacidad: 7 }),
      insightRow("6 PM", { demanda: 13, capacidad: 7 }),
      insightRow("8 PM", { demanda: 5, capacidad: 7 }),
    ],
    views: INSIGHT_VIEW_PRESETS.temporalSinVelas,
    badge: {
      mode: "max",
      label: "Pico",
      tone: "red",
      seriesKey: "demanda",
    },
  },
  {
    id: "control-caja",
    title: "Control de caja y diferencias",
    subtitle: "Diferencia esperada vs contada — último mes",
    icon: Landmark,
    group: "operacion",
    valueKind: "currency",
    nameLabel: "Caja",
    series: [
      { key: "diferencia", label: "Diferencia", color: INSIGHT_COLORS.gastos },
    ],
    rows: [
      insightRow("Caja principal", { diferencia: -42000 }, "8 cierres"),
      insightRow("Caja 2", { diferencia: 3000 }, "6 cierres"),
      insightRow("Caja barra", { diferencia: -8000 }, "5 cierres"),
      insightRow("Caja terraza", { diferencia: 1500 }, "4 cierres"),
    ],
    views: ["barHorizontal", "bar", "tabla", "line", "matriz"],
    colorBySign: true,
    badge: { mode: "min", label: "Mayor descuadre", tone: "red" },
    showTotal: false,
  },
];
